/*
 * Copyright (c) 2026. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/**
 * planitem-multi-invulling.ts
 * 30-12-2025 - Junie (AI) - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 30-12-2025
 *
 * Helper bij planbord, die al permissies heeft gecheckt
 */

import {Component, Input, ViewChild} from '@angular/core';
import {Appscherm} from '../../abstracts/appscherm';
import {Planitem} from '../../../models/planitem';
import {hash} from '../../../app/types';
import {FiltertoolInstantie} from '../../../services/filtertool/filtertool-instantie';
import {Basemodel} from '../../../models/basemodel';
import {PlanitemInvullingComponent} from '../planitem-invulling.component';
import {PersoneelPlanitem} from '../../../models/personeel_planitem';
import {DateTime} from 'luxon';

@Component({
    templateUrl: './planitem-multi-invulling.html',
    styleUrls: ['./planitem-multi-invulling.scss']
})
export class PlanitemMultiInvulling extends Appscherm {

    /**
     * Referentie naar de invullingcomponent voor personeelslogica.
     */
    @ViewChild(PlanitemInvullingComponent) invullingComponent?: PlanitemInvullingComponent;

    /**
     * Het planitem dat als basis dient voor de multi-invulling
     */
    private _record: Planitem;
    @Input() set record(v: Planitem) {
        this._record = v?.fullClone() ?? null; //klonen, want invullingscomponent gaat aan de PPI's rommelen
        this.log.debug(`Record`, v);
        void this.naBijwerkenRecord();
    }

    get record(): Planitem {
        return this._record;
    }

    /**
     * De records die geselecteerd kunnen worden
     */
   selectieDiensten: Planitem[] = [];

    /**
     * Lookups voor het invullingscomponent
     */
    @Input() lookups: hash<Basemodel[]> = {};

    /**
     * Hashes voor snelle lookup
     */
    @Input() lookupHashes: hash<hash<Basemodel>> = {};

    /**
     * Filtertool instantie
     */
    @Input() filtertool: FiltertoolInstantie | null = null;

    /**
     * Alle items per datum voor overlapcheck
     */
    @Input() allitemsPerdate: hash<Planitem[]> = {};

    /**
     * Negeer planbordfilter vlag
     */
    @Input() negeerFilter: boolean = false;


    /**
     * Dit scherm is echt een hulp bij planbord - we verwachten dat de user alles mag schrijven.
     */
    constructor() {
        super('debug');
       //we hebben weinig gedoe met rechten, want zijn echt secondair aan planbord
    }

    /**
     * UI: is er door een actieve filter iets weggefilterd?
     */
    get ietsWeggefilterd(): boolean {
        return this.invullingComponent?.personeelWeggefilterd;
    }

    /**
     * Record is bijgewerkt en ook echt gewijzigd qua id. Haal gerelateerde records op en synchroniseer met het invullingcomponent
     */
    private async naBijwerkenRecord(): Promise<void> {
        if (this.record) {
            //omdat het record echt gewijzigd is, moeten we heel selectieDiensten opnieuw maken
            //we doen nóg een full clone van het record, zodat hij onafhankelijk is van de verdere invulling
            const recordKloon = this.record.fullClone();

            const nu = DateTime.now().toSQL();
            this.selectieDiensten = [recordKloon, ... await this.record.volgendeGelijke(undefined, true)].filter(dienst => dienst.startdatetime > nu);
            recordKloon._tags.selecteer = true; //deze alleen standaard ingesteld.
        }
        else {
            this.selectieDiensten = [];
        }

        //We plannen de synchronisatie in de volgende tick zodat alle input-bindings
        // (zoals lookups en filtertool) verwerkt zijn door Angular.
        setTimeout(() => {
            this.log.debug(`Synchroniseer invullingComponent`, this.invullingComponent);
            if (this.invullingComponent) {
                this.invullingComponent.synchroniseer();
            }
        });
    }

    get geselecteerdeDiensten(): Planitem[] {
        return (this.selectieDiensten || []).filter(dienst => dienst._tags.selecteer);
    }

    /**
     * True als de opgegeven dienst ons origineel is
     * @param dienst
     */
    isOrigineel(dienst: Planitem): boolean {
        return dienst.id === this.record?.id;
    }
    /**
     * Geeft true als er een dienst is met invulling, met de originele uitgezonderd.
     */
    get alIngevuld(): boolean {
        return this.selectieDiensten.some(dienst => dienst.heeftInvulling);
    }

    /**
     * True als van de -geselecteerde- diensten er verschillende capaciteiten zijn
     */
    get wisselendeCapaciteit(): boolean {
        return this.geselecteerdeDiensten.some(dienst1 => this.geselecteerdeDiensten.some(dienst2 => dienst1.capacity !== dienst2.capacity));
    }

    /**
     * Selectieknoppen voor snelle selecties
     * @param selectieType
     */
    selectie(selectieType: 'alles'|'niets'|'1 week'|'6 weken'|'jaar'): void {
        //diensten zijn al gefilterd op vandaag of verder
        let tm:string;
        const nu = DateTime.now();
        switch (selectieType) {
            case '1 week':
                tm = nu.plus({weeks: 1}).toSQLDate();
                break;
            case '6 weken':
                tm = nu.plus({weeks: 6}).toSQLDate();
                break;
            case 'jaar':
                tm = nu.plus({years: 1}).toSQLDate();
                break;
        }

        for (let dienst of this.selectieDiensten) {
            let s: boolean = false;
            switch (selectieType) {
                case 'alles':
                    s = true;
                    break;
                case 'niets':
                    s = false;
                    break;
                default:
                    s = dienst.startdate <= tm;
            }
            dienst._tags.selecteer = s;
        }
    }

    //we checken niet op dirty: elke invulling inclusief alles weg is goed

    /**
     * Voer de wijzigingen door
     */
    public async uitvoeren(): Promise<void> {
        let confirmtekst = `We gaan de invulling uitvoeren op ${this.geselecteerdeDiensten.length} dienst${this.geselecteerdeDiensten.length  > 1 ? 'en':''}.`;
        if (this.wisselendeCapaciteit)
        {
            confirmtekst += ` Deze diensten hebben verschillende capaciteiten, we vullen elke dienst maximaal tot de bij die dienst opgegeven capaciteit.`;
        }
        if (! this.record.heeftInvulling) {
            confirmtekst += ` De bestaande invulling van de geselecteerde diensten wordt verwijderd.`;
        }
        confirmtekst += `<br><br>Weet je zeker dat je wilt doorgaan?`;
        if (await this.api.confirm("Doorvoeren invulling",confirmtekst)) {
            let gewijzigd = 0;
            let mislukt = 0;
            let ingediend = 0;
            try {
                this.api.block(); //block om het geheel
                for(let dienst of this.geselecteerdeDiensten) {
                    //al hetzelfde?
                    //de originele dienst is in geselecteerdeDiensten een kloon, dus die heeft nog de originele invulling
                    if (dienst.personeel_planitem.length === this.record.personeel_planitem.length &&
                        dienst.personeel_planitem.every(extrappi => this.record.personeel_planitem.some(recppi =>
                                recppi.personeelvast_id === extrappi.personeelvast_id &&
                                (recppi.leverancier_id === null || recppi.leverancier_id === extrappi.leverancier_id)
                            )
                        )
                    ) {
                        continue; //niks te doen
                    }
                    if (dienst.personeel_planitem?.some(ppi => ppi.inkooporder || ppi.ingediend)) {
                        ingediend++; //skip ingediende dienst
                        continue;
                    }
                    //maximaal de capaciteit van de dienst vullen
                    dienst.personeel_planitem = this.record.personeel_planitem
                        .slice(0, dienst.capacity || this.record.personeel_planitem.length)
                        .map(ppi =>
                            PersoneelPlanitem.createNew(dienst, {
                                personeelvast_id: ppi.personeelvast_id,
                                planitem_id: dienst.id,
                                leverancier_id: ppi.leverancier_id
                            })
                        );
                    //en dit opslaan
                    try {
                        await dienst.save();
                        gewijzigd++;
                    } catch {
                        //toast is al geweest
                        mislukt++;
                    }
                }
            }
            finally {
                this.api.unblock();
            }
            if (mislukt) {
                this.api.toast(`De invulling van ${mislukt} ${mislukt > 1 ? 'diensten' : 'dienst'} kon niet aangepast worden. Controleer de diensten handmatig.`, 'warning');

            }
            if (ingediend) {
                this.api.toast(`De invulling van  ${ingediend} ${ingediend > 1 ? 'diensten' : 'dienst'} is niet aangepast omdat hier al werkbonnen of inkooporders voor bestaan. Controleer de diensten handmatig.`, 'warning');
            }
            if (gewijzigd && (!mislukt) && (!ingediend)) {
                this.api.toast(`De invulling van ${gewijzigd} ${gewijzigd > 1 ? 'diensten' : 'dienst'} is aangepast.`);
            }
            this.closeWindow(true);
        }
    }

    /**
     * Wijziging van de 'negeerFilter' schakelaar; herbereken de personeelslijst direct.
     */
    public negeerFilterChanged(): void {
        this.invullingComponent?.herberekenPersoneelFilter();
    }
}
